import { ChangeSource } from './types';

interface ChangeNotifierHooks {
    /** Runs when the listener count goes 0 -> 1. */
    onFirstSubscribe?: () => void;
    /** Runs when the listener count goes 1 -> 0. */
    onLastUnsubscribe?: () => void;
}

/**
 * Shared {@link ChangeSource} implementation for adapters: a monotonic
 * version counter with listener fan-out. The adapter calls {@link notify}
 * when its truth changes; the hooks let an adapter hold an upstream
 * observer only while someone is listening.
 *
 * `subscribe`/`getVersion` are instance-stable arrow properties, so the
 * object can be handed to `useSyncExternalStore` directly.
 */
export class ChangeNotifier implements ChangeSource {
    private version = 0;
    // An array, not a Set: two subscriptions of the same function reference are
    // two independent subscriptions, so N subscribes need N unsubscribes before
    // onLastUnsubscribe fires (a Set would collapse them and tear down early).
    private readonly listeners: Array<() => void> = [];

    public constructor(private readonly hooks: ChangeNotifierHooks = {}) {}

    public subscribe = (listener: () => void): (() => void) => {
        this.listeners.push(listener);
        if (this.listeners.length === 1) {
            this.hooks.onFirstSubscribe?.();
        }
        let removed = false;
        return () => {
            if (removed) {
                return;
            }
            removed = true;
            const index = this.listeners.indexOf(listener);
            if (index !== -1) {
                this.listeners.splice(index, 1);
            }
            if (this.listeners.length === 0) {
                this.hooks.onLastUnsubscribe?.();
            }
        };
    };

    public getVersion = (): number => this.version;

    public get hasListeners(): boolean {
        return this.listeners.length > 0;
    }

    public notify(): void {
        this.version += 1;
        // Copy first: a listener may unsubscribe during fan-out.
        [...this.listeners].forEach((listener) => listener());
    }
}
